import React, { useState, useCallback, useEffect } from 'react';
import { HTTPError } from 'ky';
import { AdminLayout } from '../../layouts/AdminLayout';
import { Button } from '../../components/atoms/Button';
import { Spinner } from '../../components/atoms/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { alertBannerService } from '../../services/alert-banner.service';
import type {
  AlertBanner,
  CreateAlertBannerDto,
  UpdateAlertBannerDto,
  AlertBannerVariant,
} from '@lnr-v2/types';
import styles from './AlertBannerPage.module.css';

async function getErrorMessage(error: unknown): Promise<string> {
  if (error instanceof HTTPError) {
    try {
      const body = await error.response.json();
      if (body.details && Array.isArray(body.details)) {
        return body.details
          .map(
            (d: { path?: string[]; message?: string }) =>
              `${d.path?.join('.') || 'field'}: ${d.message}`
          )
          .join(', ');
      }
      return body.error || body.message || error.message;
    } catch {
      return `Server error (${error.response.status})`;
    }
  }
  return error instanceof Error ? error.message : 'Unknown error';
}

const PAGE_OPTIONS = [
  { value: 'home', label: 'Home' },
  { value: 'agency', label: 'Agency' },
  { value: 'cases', label: 'Cases' },
  { value: 'case-detail', label: 'Case Detail' },
  { value: 'codex', label: 'Codex' },
  { value: 'contact', label: 'Contact' },
];

const VARIANT_OPTIONS: { value: AlertBannerVariant; label: string }[] = [
  { value: 'INFO', label: 'Info' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'PROMO', label: 'Promo' },
];

const variantStyleMap: Record<AlertBannerVariant, string> = {
  INFO: styles.variantInfo,
  WARNING: styles.variantWarning,
  PROMO: styles.variantPromo,
};

const previewStyleMap: Record<AlertBannerVariant, string> = {
  INFO: styles.previewInfo,
  WARNING: styles.previewWarning,
  PROMO: styles.previewPromo,
};

function getBannerStatus(
  banner: AlertBanner
): 'active' | 'scheduled' | 'inactive' {
  if (!banner.isActive) return 'inactive';
  const now = new Date();
  if (banner.startDate && new Date(banner.startDate) > now) return 'scheduled';
  if (banner.endDate && new Date(banner.endDate) < now) return 'inactive';
  return 'active';
}

interface FormState {
  textEN: string;
  textFR: string;
  linkUrl: string;
  linkLabelEN: string;
  linkLabelFR: string;
  variant: AlertBannerVariant;
  targetPages: string[];
  startDate: string;
  endDate: string;
}

const emptyForm: FormState = {
  textEN: '',
  textFR: '',
  linkUrl: '',
  linkLabelEN: '',
  linkLabelFR: '',
  variant: 'INFO',
  targetPages: [],
  startDate: '',
  endDate: '',
};

export const AlertBannerPage: React.FC = () => {
  const { showToast } = useToast();
  const [banners, setBanners] = useState<AlertBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await alertBannerService.getAll(1, 50);
      setBanners(response.data?.items || []);
    } catch (err) {
      const msg = await getErrorMessage(err);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (banner: AlertBanner) => {
    setEditingId(banner.id);
    setForm({
      textEN: banner.textEN,
      textFR: banner.textFR,
      linkUrl: banner.linkUrl || '',
      linkLabelEN: banner.linkLabelEN || '',
      linkLabelFR: banner.linkLabelFR || '',
      variant: banner.variant,
      targetPages: banner.targetPages,
      startDate: banner.startDate ? banner.startDate.slice(0, 16) : '',
      endDate: banner.endDate ? banner.endDate.slice(0, 16) : '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.textEN.trim() || !form.textFR.trim()) {
      showToast('Text EN and FR are required', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const dto: UpdateAlertBannerDto = {
          textEN: form.textEN,
          textFR: form.textFR,
          linkUrl: form.linkUrl || null,
          linkLabelEN: form.linkLabelEN || null,
          linkLabelFR: form.linkLabelFR || null,
          variant: form.variant,
          targetPages: form.targetPages,
          startDate: form.startDate
            ? new Date(form.startDate).toISOString()
            : null,
          endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
        };
        await alertBannerService.update(editingId, dto);
        showToast('Banner updated', 'success');
      } else {
        const dto: CreateAlertBannerDto = {
          textEN: form.textEN,
          textFR: form.textFR,
          linkUrl: form.linkUrl || undefined,
          linkLabelEN: form.linkLabelEN || undefined,
          linkLabelFR: form.linkLabelFR || undefined,
          variant: form.variant,
          targetPages: form.targetPages,
          startDate: form.startDate
            ? new Date(form.startDate).toISOString()
            : undefined,
          endDate: form.endDate
            ? new Date(form.endDate).toISOString()
            : undefined,
        };
        await alertBannerService.create(dto);
        showToast('Banner created', 'success');
      }
      setModalOpen(false);
      fetchBanners();
    } catch (err) {
      const msg = await getErrorMessage(err);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (banner: AlertBanner) => {
    try {
      if (banner.isActive) {
        await alertBannerService.deactivate(banner.id);
        showToast('Banner deactivated', 'success');
      } else {
        await alertBannerService.activate(banner.id);
        showToast('Banner activated', 'success');
      }
      fetchBanners();
    } catch (err) {
      const msg = await getErrorMessage(err);
      showToast(msg, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await alertBannerService.delete(id);
      showToast('Banner deleted', 'success');
      setDeleteConfirm(null);
      fetchBanners();
    } catch (err) {
      const msg = await getErrorMessage(err);
      showToast(msg, 'error');
    }
  };

  const handlePageToggle = (page: string) => {
    setForm((prev) => ({
      ...prev,
      targetPages: prev.targetPages.includes(page)
        ? prev.targetPages.filter((p) => p !== page)
        : [...prev.targetPages, page],
    }));
  };

  return (
    <AdminLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Alert Banners</h1>
          <Button variant="primary" size="sm" onClick={openCreate}>
            New Banner
          </Button>
        </div>

        {loading ? (
          <Spinner />
        ) : banners.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>No banners yet</p>
            <p className={styles.emptyText}>
              Create a banner to display announcements on the website.
            </p>
            <Button variant="primary" size="sm" onClick={openCreate}>
              Create First Banner
            </Button>
          </div>
        ) : (
          <div className={styles.list}>
            {banners.map((banner) => {
              const status = getBannerStatus(banner);
              return (
                <div
                  key={banner.id}
                  className={`${styles.card} ${status === 'active' ? styles.cardActive : ''}`}
                >
                  <div className={styles.cardContent}>
                    <div className={styles.cardMeta}>
                      <span
                        className={`${styles.badge} ${
                          status === 'active'
                            ? styles.badgeActive
                            : status === 'scheduled'
                              ? styles.badgeScheduled
                              : styles.badgeInactive
                        }`}
                      >
                        {status}
                      </span>
                      <span
                        className={`${styles.variantBadge} ${variantStyleMap[banner.variant]}`}
                      >
                        {banner.variant}
                      </span>
                    </div>
                    <p className={styles.cardText}>{banner.textEN}</p>
                    <p className={styles.cardTextSecondary}>{banner.textFR}</p>
                    {banner.targetPages.length > 0 && (
                      <p className={styles.cardPages}>
                        Pages: {banner.targetPages.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className={styles.cardActions}>
                    <button
                      className={styles.iconBtn}
                      title={banner.isActive ? 'Deactivate' : 'Activate'}
                      onClick={() => handleToggleActive(banner)}
                    >
                      {banner.isActive ? '⏸' : '▶'}
                    </button>
                    <button
                      className={styles.iconBtn}
                      title="Edit"
                      onClick={() => openEdit(banner)}
                    >
                      ✏️
                    </button>
                    <button
                      className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                      title="Delete"
                      onClick={() => setDeleteConfirm(banner.id)}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        {modalOpen && (
          <div className={styles.modal} onClick={() => setModalOpen(false)}>
            <div
              className={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className={styles.modalTitle}>
                {editingId ? 'Edit Banner' : 'New Banner'}
              </h2>

              {/* Live preview */}
              {form.textEN && (
                <div
                  className={`${styles.preview} ${previewStyleMap[form.variant]}`}
                >
                  <span>{form.textEN}</span>
                  {form.linkUrl && form.linkLabelEN && (
                    <span className={styles.previewLink}>
                      {form.linkLabelEN}
                    </span>
                  )}
                </div>
              )}

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Text EN *</label>
                  <textarea
                    className={styles.formTextarea}
                    value={form.textEN}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, textEN: e.target.value }))
                    }
                    maxLength={300}
                    placeholder="Banner message in English"
                  />
                  <span className={styles.charCount}>
                    {form.textEN.length}/300
                  </span>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Text FR *</label>
                  <textarea
                    className={styles.formTextarea}
                    value={form.textFR}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, textFR: e.target.value }))
                    }
                    maxLength={300}
                    placeholder="Message du bandeau en fran\u00e7ais"
                  />
                  <span className={styles.charCount}>
                    {form.textFR.length}/300
                  </span>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Link URL (optional)
                  </label>
                  <input
                    className={styles.formInput}
                    value={form.linkUrl}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, linkUrl: e.target.value }))
                    }
                    placeholder="https://..."
                  />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Link Label EN</label>
                    <input
                      className={styles.formInput}
                      value={form.linkLabelEN}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, linkLabelEN: e.target.value }))
                      }
                      maxLength={100}
                      placeholder="Learn more"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Link Label FR</label>
                    <input
                      className={styles.formInput}
                      value={form.linkLabelFR}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, linkLabelFR: e.target.value }))
                      }
                      maxLength={100}
                      placeholder="En savoir plus"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Variant</label>
                  <select
                    className={styles.formSelect}
                    value={form.variant}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        variant: e.target.value as AlertBannerVariant,
                      }))
                    }
                  >
                    {VARIANT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Target Pages (empty = all pages)
                  </label>
                  <div className={styles.checkboxGroup}>
                    {PAGE_OPTIONS.map((opt) => (
                      <label key={opt.value} className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={form.targetPages.includes(opt.value)}
                          onChange={() => handlePageToggle(opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Start Date (optional)
                    </label>
                    <input
                      type="datetime-local"
                      className={styles.formInput}
                      value={form.startDate}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, startDate: e.target.value }))
                      }
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      End Date (optional)
                    </label>
                    <input
                      type="datetime-local"
                      className={styles.formInput}
                      value={form.endDate}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, endDate: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.modalActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {deleteConfirm && (
          <div className={styles.confirmOverlay}>
            <div className={styles.confirmBox}>
              <p className={styles.confirmText}>
                Are you sure you want to delete this banner? This action cannot
                be undone.
              </p>
              <div className={styles.confirmActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(deleteConfirm)}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
